import MedicationTile from 'Components/DashboardTile/TileContentMaker/components/MedicationTile';
import { isPartialDate } from 'neuro-utils';
import { intersperse } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { filterMedicationsOverlapping } from 'Store/documents/helpers/documentFilters';
import { useAppSelector } from 'Store/index';
import { formatPartialDate } from 'Utility/date';
import { filterByDocumentType } from 'Utility/documentHandling';
import { sortDocuments } from 'Utility/randomUtil';
import { getAtcCodesByPlatform } from '../Document/config';
import { getDiseaseModifyingMedications } from '../utils';

// Return latest SMA/DMD medication sorted its administration dates
const findLatestSmaDmdMedication = (
  medicationSettings: IOrganizationNeuroSettings['medicationSettings'] | undefined,
  platform: Platform,
  documents: IDocumentWithTypeV2<'medication'>[],
): IDocumentWithTypeV2<'medication'>[] => {
  /** Medications visualized in tile are different from graph, so define them here */
  const atcCodesToVisualize = getAtcCodesByPlatform(medicationSettings, platform, { categories: ['tile'] });

  const medications = documents.filter(document => {
    if (!document.atc) return false;
    return atcCodesToVisualize.includes(document.atc);
  });

  return medications;
};

const dataPicker = (document: IDocumentWithTypeV2<'medication'>, fm: (id: string) => string) => {
  const dateFieldName = isPartialDate(document.usageStartDate) ? 'usageStartDate' : 'startDate';
  const dateValue = document[dateFieldName];
  return {
    title: fm(document.hasEnded?.[0] === true ? 'medication.tileEnded' : 'medication.tileStarted'),
    medicationName: document.medicationName,
    date: isPartialDate(dateValue) ? formatPartialDate(dateValue) : null,
    endDate:
      isPartialDate(document.endDate) && document.hasEnded?.[0] === true ? formatPartialDate(document.endDate) : null,
    endReasons: intersperse(
      ', ',
      (document.endReason ?? []).map(reason => fm('medication.opts.' + reason)),
    ),
    key: document._id,
  };
};

const GeneralTile = ({
  documents,
}: {
  documents: Array<IDocumentWithTypeV2<'medication'>>;
  platform: Platform;
}): React.JSX.Element => {
  const platformMedications = filterByDocumentType(documents, 'medication').filter(d => d.hasEnded?.[0] !== true);
  const platformMedicationsEnded = filterByDocumentType(documents, 'medication').filter(d => d.hasEnded?.[0] === true);

  const latestMedication = platformMedications.sort((a, b) =>
    sortDocuments([{ type: 'date', sortField: 'startDate' }])(a, b),
  )[0];
  const latestMedicationEnded = platformMedicationsEnded.sort((a, b) =>
    sortDocuments([{ type: 'date', sortField: 'endDate' }])(a, b),
  )[0];

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <React.Fragment>
      {latestMedication || latestMedicationEnded ? (
        <MedicationTile
          data={[
            ...(latestMedication ? [dataPicker(latestMedication, fm)] : []),
            ...(latestMedicationEnded ? [dataPicker(latestMedicationEnded, fm)] : []),
          ]}
        />
      ) : (
        <></>
      )}
    </React.Fragment>
  );
};

const MedicationDash = ({ documents: documentsRedux }: IOwnProps): React.JSX.Element | null => {
  const clonedDocs = structuredClone(documentsRedux);

  const orgSettings = useAppSelector(s => s.settings.orgSettings);

  const documents = orgSettings.medicationHideOverlapping
    ? filterMedicationsOverlapping(filterByDocumentType(clonedDocs, 'medication'), orgSettings['medicationSettings'])
    : filterByDocumentType(clonedDocs, 'medication');

  // Only use documents owned by current organization
  const orgId = useAppSelector((s: IState) => s.session.data?.orgid);
  const selectedMedicationOrg = useAppSelector((s: IState) => s.session.selectedMedicationOrg) ?? orgId;
  const thisOrgDocuments = documents.filter(
    d => d._ownerOrg === selectedMedicationOrg || Object.keys(d._pastOwners).some(org => org === selectedMedicationOrg),
  );
  const platform = useAppSelector(s => s.session.platforms?.selected);

  const studyMedications = thisOrgDocuments.filter(d => d.isClinicalStudy);

  const medicationSettings = useAppSelector((s: IState) => s.settings.orgSettings.medicationSettings);

  return platform ? (
    <React.Fragment>
      <GeneralTile
        documents={
          platform === 'sma' || platform === 'dmd'
            ? [...findLatestSmaDmdMedication(medicationSettings, platform, thisOrgDocuments), ...studyMedications]
            : [...getDiseaseModifyingMedications(medicationSettings, thisOrgDocuments, platform), ...studyMedications]
        }
        platform={platform}
      />
    </React.Fragment>
  ) : null;
};

interface IOwnProps {
  documents: IDocumentWithTypeV2<
    'medication' | 'adverseEffect' | 'supplementalTreatment' | 'procedure' | 'periodWithoutMedication'
  >[];
}

export default MedicationDash;
